import React, { Component } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import * as Cookies from 'es-cookie';
import { ADM_MODAL_HEADER, ADM_MODAL_TITLE, ADM_MODAL_TITLE_SUB, ADM_MODAL_BODY, ADM_MODAL_HEADER_DOT } from './admissionModalStyles';

// Classes that require a stream selection (e.g. Intermediate 1st/2nd year)
const STREAM_CLASS_IDS = [15, 16];
const isStreamClass = (classId) => STREAM_CLASS_IDS.includes(parseInt(classId));

const GET_ADMISSION_DATA = gql`
  query($institution_id: Int!, $branch_id: Int!, $form_id: Int!) {
    getExamClasses {
      id
      class
    }
    getAcademicYears {
      id
      academic_year
    }
    getStreams {
      id
      stream
      classes
    }
    getBranches(institution_id: $institution_id, branch_id: $branch_id) {
      id
      branch_name
      sections {
        id
        section_name
        class_id
        stream_id
      }
    }
    getEnquiryFormFields(form_id: $form_id) {
      id
      field_name
      options
    }
  }
`;

const MARK_MULTIPLE_AS_ADMITTED = gql`
  mutation($students: [BulkStudentAdmitInput!]!, $class_id: Int, $stream_id: Int, $section_id: Int, $academic_year_id: Int) {
    markMultipleAsAdmitted(
      students: $students,
      class_id: $class_id,
      stream_id: $stream_id,
      section_id: $section_id,
      academic_year_id: $academic_year_id
    )
  }
`;

class BulkAdmitModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      submitting: false,
      data: { classes: [], sections: [] },
      selection: { class_id: '', stream_id: '', section_id: '' },
      admissionNumbers: {},
      isAutoGenerate: false,
      error: ''
    };
  }

  componentDidMount() {
    if (this.props.show && this.props.students && this.props.students.length > 0) {
      this.fetchData();
      this.initializeAdmissionNumbers(this.props.students);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.show && this.props.students && this.props.students.length > 0) {
      const modalJustOpened = !prevProps.show;
      const studentsChanged = prevProps.students !== this.props.students;

      if (modalJustOpened || studentsChanged) {
        this.fetchData();
        this.initializeAdmissionNumbers(this.props.students);
      }
    }
  }

  initializeAdmissionNumbers = (students) => {
    const initialAdmNos = {};
    students.forEach(s => { initialAdmNos[s.id] = ''; });
    this.setState({ 
      admissionNumbers: initialAdmNos,
      selection: { class_id: '', stream_id: '', section_id: '' },
      isAutoGenerate: false,
      error: ''
    });
  };

  fetchData = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const instId = parseInt(Cookies.get('institutionid'));
      const branchId = parseInt(this.props.students[0].branch_id);

      const formId = parseInt(this.props.students[0].enquiry_form_id);
      const result = await this.props.client.query({
        query: GET_ADMISSION_DATA,
        variables: { institution_id: instId, branch_id: branchId, form_id: formId },
        fetchPolicy: 'network-only'
      });

      const branches = result.data.getBranches || [];
      const currentBranch = branches[0];
      const sections = currentBranch ? currentBranch.sections : [];

      // Map unique classes from getExamClasses based on branch sections (Attendance Pattern)
      const activeClassIds = [...new Set(sections.map(s => parseInt(s.class_id)))];
      const allGlobalClasses = result.data.getExamClasses || [];
      const classes = allGlobalClasses.filter(c => activeClassIds.includes(parseInt(c.id)));
      const academicYears = result.data.getAcademicYears || [];
      const streams = result.data.getStreams || [];
      const fields = result.data.getEnquiryFormFields || [];

      // Auto-select class based on the first student's enquiry_course_id
      let defaultClassId = '';
      const courseField = fields.find(f => {
        const lbl = (f.field_name || "").toLowerCase().replace(/_/g, ' ').trim();
        return lbl === "course name" || lbl === "enquiry class";
      });

      if (courseField && courseField.options) {
        try {
          const config = typeof courseField.options === 'string' 
            ? JSON.parse(courseField.options) 
            : courseField.options;
          const studentCourseId = this.props.students[0].enquiry_course_id;
          const mappedClass = config.find(c => parseInt(c.value) === parseInt(studentCourseId));
          if (mappedClass && mappedClass.class_id) {
            defaultClassId = String(mappedClass.class_id);
          }
        } catch (e) {
          console.error("Error parsing field config:", e);
        }
      }

      this.setState({
        data: {
          classes: classes,
          sections: sections,
          academicYears: academicYears,
          streams: streams
        },
        selection: { ...this.state.selection, class_id: defaultClassId, stream_id: '', section_id: '' },
        loading: false
      });
    } catch (err) {
      console.error("Error fetching admission data:", err);
      this.setState({ error: "Failed to load classes and sections.", loading: false });
    }
  };

  handleAdmit = async () => {
    const { selection, admissionNumbers } = this.state;
    const { students, client, onSuccess, onHide } = this.props;

    if (!selection.class_id || !selection.section_id) {
      this.setState({ error: "Please select both class and section." });
      return;
    }

    if (isStreamClass(selection.class_id) && !selection.stream_id) {
      this.setState({ error: "Please select a stream." });
      return;
    }

    // validate admission numbers
    if (!this.state.isAutoGenerate) {
      for (const student of students) {
        const admNo = admissionNumbers[student.id];
        if (!admNo || admNo.trim() === '') {
          this.setState({ error: "Please enter an admission number for all students." });
          return;
        }
      }
    }

    // construct the mapped array
    const mappedStudents = students.map(s => ({
      id: parseInt(s.id),
      admission_no: this.state.isAutoGenerate ? '' : admissionNumbers[s.id].trim()
    }));

    this.setState({ submitting: true, error: '' });
    try {
      await client.mutate({
        mutation: MARK_MULTIPLE_AS_ADMITTED,
        variables: {
          students: mappedStudents,
          class_id: parseInt(selection.class_id),
          stream_id: isStreamClass(selection.class_id) && selection.stream_id ? parseInt(selection.stream_id) : null,
          section_id: parseInt(selection.section_id),
          academic_year_id: 2
        }
      });
      if (onSuccess) onSuccess();
      onHide();
    } catch (err) {
      this.setState({ error: err.message?.replace('GraphQL error: ', '') || 'Failed to admit students.', submitting: false });
      return;
    }
    this.setState({ submitting: false });
  };

  render() {
    const { show, onHide, students } = this.props;
    const { loading, submitting, data, selection, admissionNumbers, error } = this.state;

    const showStream = isStreamClass(selection.class_id);

    // Streams available for the selected class, derived from the sections' stream_id
    const streamOptions = showStream
      ? [...new Set(
          data.sections
            .filter(s => parseInt(s.class_id) === parseInt(selection.class_id) && s.stream_id)
            .map(s => s.stream_id)
        )]
          .map(sid => (data.streams || []).find(st => Number(st.id) === Number(sid)))
          .filter(Boolean)
      : [];

    const filteredSections = selection.class_id
      ? data.sections
          .filter(s => parseInt(s.class_id) === parseInt(selection.class_id))
          .filter(s => !showStream || !selection.stream_id || Number(s.stream_id) === Number(selection.stream_id))
      : data.sections;

    return (
      <Modal show={show} onHide={onHide} centered size="md">
        <Modal.Header closeButton style={ADM_MODAL_HEADER}>
          <Modal.Title style={ADM_MODAL_TITLE}>
            <span style={ADM_MODAL_HEADER_DOT} />
            <i className="fal fa-users" />
            <span>
              Finalize Bulk Admission
              <span style={ADM_MODAL_TITLE_SUB}>Assign roll numbers and class/section to selected students</span>
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={ADM_MODAL_BODY}>
          <div className="mb-4 text-center">
            <h5 className="mb-1">{students?.length} Students Selected</h5>
            <p className="text-muted small">Generate Roll Numbers and Assign Class/Section for all</p>
          </div>

          {error && <div className="alert alert-danger py-2 small">{error}</div>}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted small">Loading options...</p>
            </div>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="small font-weight-bold">Select Class</Form.Label>
                <Form.Control
                  as="select"
                  value={selection.class_id}
                  onChange={(e) => this.setState({ selection: { ...selection, class_id: e.target.value, stream_id: '', section_id: '' } })}
                  className="custom-select"
                >
                  <option value="">-- Choose Class --</option>
                  {data.classes.map(c => (
                    <option key={c.id} value={c.id}>{c.class}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              {showStream && (
                <Form.Group className="mb-3">
                  <Form.Label className="small font-weight-bold">Select Stream</Form.Label>
                  <Form.Control
                    as="select"
                    value={selection.stream_id}
                    onChange={(e) => this.setState({ selection: { ...selection, stream_id: e.target.value, section_id: '' } })}
                    className="custom-select"
                  >
                    <option value="">-- Choose Stream --</option>
                    {streamOptions.map(st => (
                      <option key={st.id} value={st.id}>{st.stream}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label className="small font-weight-bold">Select Section</Form.Label>
                <Form.Control
                  as="select"
                  value={selection.section_id}
                  onChange={(e) => this.setState({ selection: { ...selection, section_id: e.target.value } })}
                  className="custom-select"
                  disabled={!selection.class_id || (showStream && !selection.stream_id)}
                >
                  <option value="">-- Choose Section --</option>
                  {filteredSections.map(s => (
                    <option key={s.id} value={s.id}>{s.section_name}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check 
                  type="switch"
                  id="auto-generate-bulk-switch"
                  label="Auto-generate Admission Numbers"
                  checked={this.state.isAutoGenerate}
                  onChange={(e) => this.setState({ isAutoGenerate: e.target.checked, error: '' })}
                />
              </Form.Group>

              {!this.state.isAutoGenerate && (
                <div className="mt-4 mb-2">
                  <h6 className="small font-weight-bold mb-2">Student Admission Numbers <span className="text-danger">*</span></h6>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="border rounded p-2 bg-light">
                    {students?.map(s => (
                      <div key={s.id} className="d-flex align-items-center mb-2">
                        <div style={{ width: '50%' }} className="text-truncate small font-weight-bold mr-2" title={s.student_name}>
                          {s.student_name}
                        </div>
                        <Form.Control 
                          type="text"
                          size="sm"
                          placeholder="Admission No"
                          value={admissionNumbers[s.id] || ''}
                          onChange={(e) => this.setState({ admissionNumbers: { ...admissionNumbers, [s.id]: e.target.value } })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <Button 
                  variant="success" 
                  block 
                  onClick={this.handleAdmit} 
                  disabled={submitting}
                  style={{ height: '45px', borderRadius: '8px', fontWeight: '600' }}
                >
                  {submitting ? (
                    <><Spinner animation="border" size="sm" className="mr-2" /> Processing...</>
                  ) : (
                    `Confirm & Admit (${students?.length})`
                  )}
                </Button>
                <Button 
                  variant="light" 
                  block 
                  onClick={onHide} 
                  className="mt-2 border"
                  style={{ height: '45px', borderRadius: '8px' }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    );
  }
}

export default withApollo(BulkAdmitModal);
